import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DataService } from '../../../services/data.service';
import { DataSourceGet } from '../../../models/datasource';

@Component({
  selector: 'app-edit-api-listener-datasource',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './edit-api-listener-datasource.component.html',
  styleUrls: ['./edit-api-listener-datasource.component.scss']
})
export class EditApiListenerDatasourceComponent implements OnInit {
  @Input() dataSource!: DataSourceGet;

  endpoint: string = '';
  token: string = '';
  isRegeneratingToken = false;
  tokenRegenerationError: string | null = null;
  tokenJustCopied = false;

  constructor(private dataService: DataService) {}

  ngOnInit(): void {
    this.parseDataSourceAccess();
    this.constructEndpoint();
  }

  private parseDataSourceAccess(): void {
    if (this.dataSource.dataSourceAccessJson) {
      try {
        const accessData = JSON.parse(this.dataSource.dataSourceAccessJson);
        // Only parse token from JSON - endpoint is constructed dynamically
        this.token = accessData.token || accessData.dataSourceToken || '';
      } catch (error) {
        console.error('Error parsing dataSourceAccessJson:', error);
      }
    }
  }

  private constructEndpoint(): void {
    // Construct endpoint dynamically from base URL and datasource ID
    // This matches how it's done during API listener creation
    if (this.dataSource.dataSourceId) {
      const baseUrl = this.dataService.getBaseUrl();
      this.endpoint = `${baseUrl}/Datasource/apilistener/storeschema/${this.dataSource.dataSourceId}`;
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.showCopyConfirmation();
    });
  }

  selectText(event: MouseEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.select();
  }

  getTokenButtonText(): string {
    if (this.isRegeneratingToken) {
      return 'Regenerating...';
    }
    if (this.tokenJustCopied) {
      return 'Copied!';
    }
    return 'Copy';
  }

  regenerateToken(): void {
    if (!this.dataSource.dataSourceId) {
      this.tokenRegenerationError = 'No data source ID available';
      return;
    }

    this.isRegeneratingToken = true;
    this.tokenRegenerationError = null;

    this.dataService.regenerateApiListenerToken(this.dataSource.dataSourceId).subscribe({
      next: (newToken) => {
        this.token = newToken;
        this.isRegeneratingToken = false;
        console.log('Token regenerated successfully');
        
        // Update the dataSourceAccessJson with new token
        this.updateDataSourceAccess(newToken);
        
        // Automatically copy the new token to clipboard
        this.copyToClipboard(newToken);
      },
      error: (error) => {
        this.isRegeneratingToken = false;
        this.tokenRegenerationError = error.message || 'Failed to regenerate token';
        console.error('Error regenerating token:', error);
      }
    });
  }

  private updateDataSourceAccess(newToken: string): void {
    if (this.dataSource.dataSourceAccessJson) {
      try {
        const accessData = JSON.parse(this.dataSource.dataSourceAccessJson);
        accessData.token = newToken;
        this.dataSource.dataSourceAccessJson = JSON.stringify(accessData);
      } catch (error) {
        console.error('Error updating dataSourceAccessJson:', error);
      }
    }
  }

  private showCopyConfirmation(): void {
    this.tokenJustCopied = true;
    setTimeout(() => {
      this.tokenJustCopied = false;
    }, 2000);
  }

  getCurlCommand(): string {
    if (!this.endpoint || !this.token) {
      return 'Missing endpoint or token';
    }

    return `curl -X POST "${this.endpoint}" \\
  -H "Authorization: Bearer ${this.token}" \\
  -H "Content-Type: application/json" \\
  -d '{"your": "data"}'`;
  }

  selectCurlText(event: MouseEvent): void {
    const preElement = event.target as HTMLPreElement;
    const range = document.createRange();
    range.selectNodeContents(preElement);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
}
